import React, { useState, useEffect, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import axios from 'axios';
import api from '../../api/api.js';
import { API_BASE_URL } from '../../config';
import { Bell, Heart, Home, MessageSquare, Plus, Search, Star, Wrench, Eye, Folder } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "../../_newprofiles/components/ui/Avatar"; 
import { Badge } from "../../_newprofiles/components/ui/Badge";
import { Button } from "../../_newprofiles/components/ui/Button"; 
import { Card, CardContent, CardHeader, CardTitle } from "../../_newprofiles/components/ui/Card";
import styles from "./_HomeUserDashboard.module.css";
import SearchFormComponent from "./SearchFormComponent";
import FeaturedBusinesses from "./_FeaturedBusinesses"; 
import NavbarLogout from "../../components/Navbar/navbar-logout";
import { jwtDecode } from 'jwt-decode';
import {  useNavigate } from 'react-router-dom';
import ModernNavbarPureCSS from "../../components/Navbar/ModernNavBarPureCSS";
import BusinessProfilePureCSS from '../../profiles/BusinessProfilePureCSS';
import JobCreationAssistant from './JobCreationAssistant'; 
import CreateProjectModal from '../../components/Projects/CreateProjectModal';
import FindContractorsModal from '../../components/Projects/Modals/FindContractorsModal';

import { getOrCreateGeneralMaintenanceProjectId } from '../../services/GeneralProjectService';
import showToast from '../../components/ShowToast';

const HomeUserDashboardPureCSS = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('projects'); 
  const [username, setUsername] = useState('');
  const [activeJobsData, setActiveJobsData] = useState([]);
  const [isLoadingActiveJobs, setIsLoadingActiveJobs] = useState(false);
  const [activeJobsError, setActiveJobsError] = useState(null);
  const [homeUserId, setHomeUserId] = useState(null);

  // State for Data Lists
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [displayProjects, setDisplayProjects] = useState([]); // Real projects (Filtered)
  const [oneOffJobs, setOneOffJobs] = useState([]); // System project jobs (Filtered)

  // Sidebar/Misc State
  const [savedBusinessesData, setSavedBusinessesData] = useState([]);
  const [isLoadingSavedBusinesses, setIsLoadingSavedBusinesses] = useState(false);
  const [savedBusinessesError, setSavedBusinessesError] = useState(null);
  const [completedJobsCount, setCompletedJobsCount] = useState(0);
  const [isLoadingCompletedJobs, setIsLoadingCompletedJobs] = useState(false);
  const [conversationCount, setConversationCount] = useState(0);
  const [isLoadingConversationCount, setIsLoadingConversationCount] = useState(false);
  const [recentMessages, setRecentMessages] = useState([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [messagesError, setMessagesError] = useState(null);
  const [loading, setLoading] = useState(false); 

  // Modals/Panels State
  const [isBusinessProfileOpen, setIsBusinessProfileOpen] = useState(false);
  const [selectedBusinessId, setSelectedBusinessId] = useState(null);
  const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] = useState(false);
  const [isFindContractorsModalOpen, setIsFindContractorsModalOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);

  // --- Handlers ---
  const handleResize = useCallback(() => setIsMobileView(window.innerWidth <= 768), []);

  const openBusinessProfile = (businessId) => {
    setSelectedBusinessId(businessId);
    setIsBusinessProfileOpen(true);
  };

  const closeBusinessProfile = () => {
    setIsBusinessProfileOpen(false);
    setSelectedBusinessId(null);
  };

  const openCreateProjectModal = () => setIsCreateProjectModalOpen(true);
  const closeCreateProjectModal = () => setIsCreateProjectModalOpen(false);

  const handlePostOneOffJob = () => {
    setIsFindContractorsModalOpen(true);
  };

  const handlePostJob = async () => {
    try {
      setIsFindContractorsModalOpen(false);
      setLoading(true);
      const projectId = await getOrCreateGeneralMaintenanceProjectId(homeUserId);
      navigate('/job-detail-new', { 
        state: { 
          projectId: projectId,
          mode: 'broadcast',
          taskName: ''
        } 
      });
    } catch (err) {
      showToast('error', "Could not initialize job. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBrowsePros = async () => {
    try {
      setIsFindContractorsModalOpen(false);
      setLoading(true);
      const projectId = await getOrCreateGeneralMaintenanceProjectId(homeUserId);
      navigate('/job-detail-new', { 
        state: { 
          projectId: projectId,
          mode: 'invite',
          taskName: ''
        } 
      });
    } catch (err) {
      showToast('error', "Could not initialize job. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getProgressBarColor = (percentage) => {
    if (percentage >= 100) return '#10B981'; // Green
    if (percentage >= 50) return '#3B82F6';  // Blue
    return '#D97706';                         // Orange/Amber
  };

  // --- Initialization ---
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        setUsername(decodedToken.UserName || 'User');
        setHomeUserId(decodedToken.nameid);
      } catch (error) {
        console.error("Error decoding token:", error);
      }
    }
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  // --- Core Data Fetching ---
  useEffect(() => {
    if (homeUserId) {
      const loadData = async () => {
        setIsLoadingProjects(true);
        setIsLoadingActiveJobs(true);
        setActiveJobsError(null); // Reset error state

        try {
          // 1. Fetch Projects
          const projRes = await api.get(`/api/Projects/getmyprojects?userId=${homeUserId}&userType=HomeUser`);
          const allProjects = projRes.data || [];

          const bucketProject = allProjects.find(p => {
             const name = p.name || p.Name;
             return name && name.toLowerCase() === "general maintenance";
          });
          const bucketId = bucketProject ? (bucketProject.projectId || bucketProject.ProjectId) : -1;

          let filteredProjects = allProjects.filter(p => {
              const pId = p.projectId || p.ProjectId;
              return pId !== bucketId;
          });

          filteredProjects.sort((a, b) => {
             const idA = a.projectId || a.ProjectId || 0;
             const idB = b.projectId || b.ProjectId || 0;
             return idB - idA;
          });

          setDisplayProjects(filteredProjects);

          // 2. Fetch Jobs
          // UPDATED: Changed from 'getmyactivejobs' to 'getmyjobs' to include Completed jobs
          // UPDATED: Added catch block to handle 404 cleanly without throwing to main catch
        const jobsRes = await axios
          .get(`${API_BASE_URL}/api/Jobs/getmyactivejobs?homeUserId=${homeUserId}`)
          .catch(err => {
            console.log("Error getmyactivejobs:", err);
            console.log("Error response:", err.response);
            console.log("Error response data:", err.response?.data);
            console.log("Error response status:", err.response?.status);

            if (err.response && err.response.status === 404) {
              return { data: [] };
            }

            throw err;
          });

        console.debug("jobsRes:", jobsRes);
        console.debug("jobsRes.data:", jobsRes?.data);

        const allJobs = jobsRes?.data || [];
        console.debug("allJobs:", allJobs);

          const soloProjId = await getOrCreateGeneralMaintenanceProjectId(homeUserId);
          console.debug("soloProjId:", soloProjId);

          const soloJobs = allJobs.filter(j => {
              const jProjId = j.projectId || j.ProjectId;
              // If bucketId matches, OR if no project ID is assigned (null/undefined/0/-1)
              return jProjId === soloProjId || !jProjId || jProjId === -1;
          });

          // Sort by CreatedAt descending (Newest first)
          soloJobs.sort((a, b) => {
             const dateA = new Date(a.created_at || a.CreatedAt || 0);
             const dateB = new Date(b.created_at || b.CreatedAt || 0);
             return dateB - dateA;
          });
          
          setOneOffJobs(soloJobs);
          setActiveJobsData(allJobs); 

        } catch (err) {
          console.error("Error loading dashboard data:", err);
          setActiveJobsError("Failed to load active jobs.");
        } finally {
          setIsLoadingProjects(false);
          setIsLoadingActiveJobs(false);
        }
      };

      const fetchSidebarData = async () => {
        setIsLoadingSavedBusinesses(true);
        setIsLoadingMessages(true);
        try {
          const [savedRes, compRes, convRes, msgRes] = await Promise.all([
            axios.get(`${API_BASE_URL}/api/BusinessProfile/getSavedBusinesses?userType=Home&userId=${homeUserId}`).catch(() => ({ data: [] })),
            axios.get(`${API_BASE_URL}/api/Jobs/getmycompletedjobcount?homeUserId=${homeUserId}`).catch(() => ({ data: 0 })),
            axios.get(`${API_BASE_URL}/api/Chat/homeUserConversationCount?homeUserId=${homeUserId}`).catch(() => ({ data: 0 })),
            api.get('/api/Chat/getRecentMessages').catch(() => ({ data: [] }))
          ]);
    
          setSavedBusinessesData(savedRes.data || []);
          setCompletedJobsCount(compRes.data || 0);
          setConversationCount(convRes.data || 0);
          setRecentMessages(msgRes.data || []);
        } catch (err) {
          console.error("Error fetching sidebar stats:", {
            message: err.message,
            status: err.response?.status,
            data: err.response?.data,
            stack: err.stack
          });
        } finally {
          setIsLoadingSavedBusinesses(false);
          setIsLoadingMessages(false);
        }
      };

      loadData();
      fetchSidebarData();
    }
  }, [homeUserId]);

    const getFirstName = (name) => {
    if (!name || name.toLowerCase() === 'user') return 'User';
    return name.split(' ')[0];
  };

  const timeSince = (date) => {
      const seconds = Math.floor((new Date() - new Date(date)) / 1000);
      let interval = seconds / 31536000;
      if (interval > 1) return Math.floor(interval) + " years ago";
      interval = seconds / 2592000;
      if (interval > 1) return Math.floor(interval) + " months ago";
      interval = seconds / 86400;
      if (interval > 1) return Math.floor(interval) + " days ago";
      interval = seconds / 3600;
      if (interval > 1) return Math.floor(interval) + " hours ago";
      interval = seconds / 60;
      if (interval > 1) return Math.floor(interval) + " minutes ago";
      return "Just now";
  };

  // Helper to determine status color
  const getStatusColor = (status) => {
    const s = (status || "").toLowerCase();
    if(s === 'completed') return '#16A34A'; // Green
    if(s === 'assigned') return '#D97706'; // Orange
    if(s === 'open') return '#2563EB'; // Blue
    return '#64748B'; // Gray
  };

  return (
    <div className={styles.dashboardContainer}>
      <header className={styles.header}>
        <div className={styles.container}>
          <div className={styles.headerContent}>
            <div className={styles.headerLeft}>
              <div className={styles.logoContainer}>
                <Home className={styles.logoIcon} />
                <span className={styles.logoText}>Build Start</span>
              </div>
              <Badge variant="secondary" className={styles.homeOwnerBadge}>Homeowner</Badge>
            </div>
            <ModernNavbarPureCSS UserName={username} prefix={''} />
          </div>
        </div>
      </header>

      <div className={styles.container}>
        <div className={styles.mainContent}>
          <div className={styles.welcomeSection}>
            <div className={styles.welcomeText}>
              <h1 className={styles.welcomeTitle}>Welcome home, {getFirstName(username)}! 🏠</h1>
              <p className={styles.welcomeSubtitle}>Manage your home improvement projects and connect with professionals</p>
            </div>
            <div className={styles.welcomeActions}>
              <button className={`${styles.qaBtn} ${styles.btnNavy}`} onClick={openCreateProjectModal}>
                <Folder size={18}/> Start New Project
              </button>
              <button className={`${styles.qaBtn} ${styles.btnGold}`} onClick={handlePostOneOffJob} disabled={loading}>
                <Wrench size={18}/> Post One-off Job
              </button>
            </div>
          </div>

          <div className={styles.quickSearchCardWrapper}>
            <SearchFormComponent />
          </div>

          {/* Stats Cards */}
          <div className={styles.statsGrid}>
            <Card className={`${styles.statCard} ${styles.statCardBlue}`}>
              <CardContent className={styles.statCardContent}>
                <div className={styles.statCardInfo}>
                  <div>
                    <p className={styles.statLabelBlue}>Active Jobs</p>
                    <p className={styles.statValue}>{isLoadingActiveJobs ? '...' : activeJobsData.length}</p>
                  </div>
                  <Wrench className={styles.statIconBlue} />
                </div>
              </CardContent>
            </Card>

            <Card className={`${styles.statCard} ${styles.statCardAmber}`}>
              <CardContent className={styles.statCardContent}>
                <div className={styles.statCardInfo}>
                  <div>
                    <p className={styles.statLabelAmber}>Saved Businesses</p>
                    <p className={styles.statValue}>{isLoadingSavedBusinesses ? '...' : savedBusinessesData.length}</p>
                  </div>
                  <Heart className={styles.statIconAmber} />
                </div>
              </CardContent>
            </Card>

            <Card className={`${styles.statCard} ${styles.statCardGreen}`}>
              <CardContent className={styles.statCardContent}>
                <div className={styles.statCardInfo}>
                  <div>
                    <p className={styles.statLabelGreen}>Conversations</p>
                    <p className={styles.statValue}>{isLoadingConversationCount ? '...' : conversationCount}</p>
                  </div>
                  <MessageSquare className={styles.statIconGreen} />
                </div>
              </CardContent>
            </Card>

            <Card className={`${styles.statCard} ${styles.statCardPurple}`}>
              <CardContent className={styles.statCardContent}>
                <div className={styles.statCardInfo}>
                  <div>
                    <p className={styles.statLabelPurple}>Completed Jobs</p>
                    <p className={styles.statValue}>{isLoadingCompletedJobs ? '...' : completedJobsCount}</p>
                  </div>
                  <Star className={styles.statIconPurple} />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className={styles.mainLayoutGrid}>
            <div className={`${styles.leftColumn} ${styles.spaceY6}`}>
              <div className={styles.tabContainer}>
                <div className={styles.tabHeader}>
                  <button
                    className={`${styles.tabButton} ${activeTab === 'projects' ? styles.activeTab : ''}`}
                    onClick={() => setActiveTab('projects')}
                  >
                    My Projects ({isLoadingProjects ? '...' : displayProjects.length})
                  </button>
                  <button 
                    className={`${styles.tabButton} ${activeTab === 'jobs' ? styles.activeTab : ''}`}
                    onClick={() => setActiveTab('jobs')}
                  >
                    One-off Jobs ({isLoadingActiveJobs ? '...' : oneOffJobs.length})
                  </button>
                </div>

                <div className={styles.tabContent}>
                  {/* --- PROJECTS TAB --- */}
                  {activeTab === 'projects' && (
                    <>
                      {isLoadingProjects && <p>Loading projects...</p>}
                      {!isLoadingProjects && displayProjects.length === 0 && (
                        <div style={{textAlign:'center', padding:'2rem', color:'#64748B'}}>
                          <p>No active projects found.</p>
                        </div>
                      )}


                    {!isLoadingProjects && displayProjects.map(project => {
                      // Handle case sensitivity safely
                      const progressVal = project.progress || project.Progress || 0;
                      const pStatus = project.status || project.Status || 'Planning';
                      
                      return (
                        <div key={project.projectId || project.ProjectId} className={styles.listItem}>
                          <div className={styles.itemMain}>
                            <h4>{project.name || project.Name}</h4>
                            <div className={styles.itemMeta}>
                              <span style={{color: '#D97706', fontWeight: 600}}>
                                Status: {pStatus}
                              </span>
                              {/* Optional: Show job count as well */}
                              <span style={{marginLeft:'10px', color:'#64748B', fontSize:'0.85rem'}}>
                                • {project.jobCount || project.JobCount || 0} Jobs
                              </span>
                            </div>
                          </div>

                          <div style={{display:'flex', alignItems:'center'}}>
                            <div className={styles.progressWrap}>
                                <span className={styles.progressText}>
                                  {progressVal}% Complete
                                </span>
                                <div className={styles.barBg}>
                                  <div 
                                    className={styles.barFill} 
                                    style={{
                                      width: `${progressVal}%`,
                                      backgroundColor: getProgressBarColor(progressVal) // Dynamic color
                                    }}
                                  ></div>
                                </div>
                            </div>
                            <button 
                              className={styles.btnAction} 
                              onClick={() => navigate(`/project/${project.projectId || project.ProjectId}`)}
                            >
                              Manage
                            </button>
                          </div>
                        </div>
                      );
                    })}

                      <div onClick={openCreateProjectModal} style={{textAlign:'center', marginTop:'1.5rem', color:'#64748B', fontSize:'0.9rem', cursor: 'pointer'}}>
                          <Plus size={16} style={{marginBottom:'-3px'}}/> Create a new project for larger renovations
                      </div>
                    </>
                  )}

                  {/* --- JOBS TAB --- */}
                  {activeTab === 'jobs' && (
                    <>
                      {isLoadingActiveJobs && <p>Loading jobs...</p>}
                      {/* Hide the error if it's null, handled by local catch */}
                      {activeJobsError && <p style={{color:'red'}}>{activeJobsError}</p>}
                      
                      {!isLoadingActiveJobs && oneOffJobs.length === 0 && (
                        <div style={{textAlign:'center', padding:'2rem', color:'#64748B'}}>
                          <p>No one-off jobs found.</p>
                        </div>
                      )}
                      {!isLoadingActiveJobs && oneOffJobs.slice(0, 15).map(job => {
                        const jobId = job.job_id || job.JobId || job.id || job.Id;
                        const jobName = job.name || job.Name || 'Untitled Job';
                        const jobStatus = job.jobStatus || job.JobStatus || job.status || job.Status;
                        const jobDate = job.created_at || job.CreatedAt;
                        const quotes = job.quoteCount || job.QuoteCount || 0;

                        return (
                          <div key={jobId} className={styles.listItem}>
                            <div className={styles.itemMain}>
                              <h4>{jobName}</h4>
                              <div className={styles.itemMeta}>
                                <span style={{color: getStatusColor(jobStatus), fontWeight: 600}}>
                                  {jobStatus}
                                </span>
                                <span>{jobDate ? new Date(jobDate).toLocaleDateString() : "N/A"}</span>
                              </div>
                            </div>
                            <div style={{textAlign:'right'}}>
                              <div style={{fontSize:'0.85rem', color:'#64748B', marginBottom:'0.5rem'}}>
                                {quotes} Quotes
                              </div>
                              <button className={styles.btnAction} onClick={() => navigate(`/job-console/${jobId}`)}>Manage</button>
                            </div>
                          </div>
                        );
                      })}
                      {!isLoadingActiveJobs && oneOffJobs.length > 15 && (
                        <div style={{textAlign:'center', marginTop:'1.5rem'}}>
                            <button 
                              onClick={() => navigate('/my-jobs')} 
                              className={styles.btnAction} 
                              style={{background:'none', color:'#2563EB', border:'1px solid #2563EB', width:'auto', padding:'8px 20px'}}
                            >
                              View All Jobs ({oneOffJobs.length})
                            </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className={`${styles.rightColumn} ${styles.spaceY6}`}>
              <FeaturedBusinesses onViewProfile={openBusinessProfile} />
              
              {/* Recent Messages Card */}
              <Card className={styles.cardBase}>
                <CardHeader>
                  <div className={styles.messagesHeader}>
                    <CardTitle className={styles.cardTitleContainer}>
                      <MessageSquare className={`${styles.cardTitleIcon} ${styles.cardTitleIconBlue}`} />
                      <span>Recent Messages</span>
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  {isLoadingMessages ? <p>Loading...</p> : recentMessages.length === 0 ? (
                    <p className={styles.textMuted}>No recent messages.</p>
                  ) : (
                    <div className={styles.messagesList}>
                      {recentMessages.slice(0, 3).map((message) => (
                        <div
                          key={message.conversationId}
                          className={`${styles.messageItem} ${message.isRead ? styles.messageItemUnread : styles.messageItemRead}`}
                          onClick={() => navigate(`/home_messages?conversationId=${message.conversationId}`)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className={styles.messageMeta}>
                            <span className={styles.messageClient}>{message.otherPartyName}</span>
                            <span className={styles.messageTime}>{timeSince(message.lastMessageTimestamp)}</span>
                          </div>
                          <p className={styles.messagePreview}>{message.lastMessageSnippet}</p>
                        </div>
                      ))}
                      <Button variant="outline" size="sm" className={styles.viewAllSavedButton} onClick={() => navigate('/home_messages')}>
                        View All Messages
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Saved Businesses Card */}
              <Card className={styles.cardBase}>
                <CardHeader>
                  <CardTitle className={styles.cardTitleContainer}>
                    <Heart className={`${styles.cardTitleIcon} ${styles.cardTitleIconBlue}`} />
                    <span>Saved Businesses</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {isLoadingSavedBusinesses ? <p>Loading...</p> : savedBusinessesData.length === 0 ? (
                    <p>No saved businesses.</p>
                  ) : (
                    <div className={styles.spaceY3}>
                      {savedBusinessesData.slice(0, 3).map((business) => (
                        <div key={business.businessId} className={styles.savedBusinessItem} onClick={() => openBusinessProfile(business.businessId)} style={{cursor:'pointer'}}>
                          <Avatar className={styles.savedBusinessAvatar}>
                            <AvatarImage src={business.profilePic || "/placeholder.svg"} />
                          </Avatar>
                          <div className={styles.savedBusinessInfo}>
                            <h4 className={styles.savedBusinessName}>{business.businessName}</h4>
                            <div className={styles.ratingContainer}>
                              <Star className={styles.ratingStarIcon} />
                              <span className={styles.ratingValue}>{business.overallRating ? business.overallRating.toFixed(1) : 'N/A'}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                      <Button variant="outline" size="sm" className={styles.viewAllSavedButton} onClick={() => navigate('/saved-businesses')}>
                        View All Saved
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      <BusinessProfilePureCSS
        isOpen={isBusinessProfileOpen}
        onClose={closeBusinessProfile}
        businessId={selectedBusinessId}
        useIDTYPE={"BID"}
      />

      <JobCreationAssistant homeUserId={homeUserId} />

      <CreateProjectModal isOpen={isCreateProjectModalOpen} onClose={closeCreateProjectModal} />
      
      <FindContractorsModal 
          isOpen={isFindContractorsModalOpen} 
          onClose={() => setIsFindContractorsModalOpen(false)} 
          onPostJob={handlePostJob} 
          onBrowsePros={handleBrowsePros} 
          taskName={"One-off Job"} 
      />
    </div>
  );
};

export default HomeUserDashboardPureCSS;